import type { ConditionPageData, ConditionCategory } from './types';
import { CATEGORY_ORDER, CATEGORY_LABELS } from './types';
import { CONDITION_META } from './meta.generated';
import type { ConditionMeta } from './meta.generated';
import { registerLoadedModule } from '../../lib/module-registry';

// Full condition modules are code-split: each one is loaded on demand by slug
// via loadConditionBySlug() instead of being statically imported here. The old
// static import graph pulled ~940KB of article text into the shared bundle on
// every page just so the header nav and search could list titles; the nav-safe
// metadata now comes from the generated meta module instead.
const conditionModules = import.meta.glob('./*/*.ts');

// The core patient-facing conditions surfaced on We Can Help and in the nav menu.
// Content/guide pages (nervous-system-101, frustrated-*, etc.) are excluded from
// navigation so the menu stays focused on actual patient conditions.
const NAV_CONDITION_SLUGS = new Set([
  // Pediatric
  'adhd-focus-issues', 'autism-neurodevelopmental', 'infant-newborn-chiropractic',
  'colic-infant-digestive', 'ear-infections', 'bedwetting-enuresis',
  'torticollis', 'sensory-processing-disorder', 'plagiocephaly',
  'tongue-tie-lip-tie', 'developmental-delays', 'speech-language-delays', 'eczema',
  // Perfect Storm pediatric set (2026-08-29)
  'allergies-kids',
  'allergies-respiratory-kids',
  'asthma-kids',
  'back-pain-kids',
  'chronic-fatigue-teen',
  'chronic-pain-kids',
  'colds-kids',
  'cyclic-vomiting-kids',
  'dyspraxia-dcd-kids',
  'erbs-palsy-birth-injury-infant',
  'failure-to-thrive-picky-eating-kids',
  'fight-or-flight-kids',
  'headaches-kids',
  'hypotonia-low-muscle-tone-kids',
  'ibs-kids',
  'immune-kids',
  'lyme-kids',
  'neck-pain-kids',
  'odd-defiant-kids',
  'posture-kids',
  'rsv-recurring-sick-infant',
  'sports-kids',
  'strabismus-kids',
  'teen-depression-exhaustion',
  'tics-tourette-kids',
  'tmj-teen',
  'toe-walking-kids',
  // Pregnancy & Women
  'breastfeeding-latch-issues', 'postpartum-recovery', 'webster-technique', 'pregnancy-back-pain',
  'womens-hormones', 'pms',
  // Neurological
  'pandas-pans', 'seizure-disorders', 'sleep-disorders',
  // Adult Pain
  'back-neck-pain', 'headaches-migraines', 'sciatica', 'carpal-tunnel',
  'tmj-jaw-pain', 'posture-tech-neck', 'vertigo-dizziness', 'whiplash-auto-injury',
  'pinched-nerves', 'disc-herniation-degenerative', 'spinal-stenosis', 'dowagers-hump',
  // General Wellness
  'allergies-respiratory', 'digestive-gi-issues', 'immune-support',
  'chronic-fatigue', 'fibromyalgia', 'gentle-chiropractic', 'asthma',
  'acid-reflux-gerd', 'ibs', 'arthritis', 'colds', 'rheumatoid-arthritis',
  // Special Populations
  'senior-care', 'pots-dysautonomia', 'sports-performance', 'lyme-disease',
  'scoliosis', 'concussion-post-concussion', 'long-covid', 'post-surgery-recovery',
]);

/**
 * Load the full data for one condition page. Resolves undefined for unknown
 * slugs. The chunk for each condition is fetched only when its page renders.
 */
export async function loadConditionBySlug(slug: string): Promise<ConditionPageData | undefined> {
  const meta = CONDITION_META.find((c) => c.slug === slug);
  if (!meta) return undefined;

  const loader = conditionModules[`${meta.module}.ts`];
  if (!loader) return undefined;

  registerLoadedModule(`src/data/conditions/${meta.module.replace(/^\.\//, '')}.ts`);
  const mod = (await loader()) as Record<string, unknown>;
  for (const value of Object.values(mod)) {
    if (
      value &&
      typeof value === 'object' &&
      (value as ConditionPageData).slug === slug
    ) {
      return value as ConditionPageData;
    }
  }
  return undefined;
}

// Conditions resolved ahead of render. The SSR prerender and the client's
// pre-hydration boot both call preloadCondition() for the route's slug, so
// the page's FIRST render (server and hydrating client alike) is the resolved
// article rather than a placeholder that would re-render after an effect.
const preloadedConditions = new Map<string, ConditionPageData | undefined>();

export async function preloadCondition(slug: string): Promise<ConditionPageData | undefined> {
  if (preloadedConditions.has(slug)) return preloadedConditions.get(slug);
  const data = await loadConditionBySlug(slug);
  preloadedConditions.set(slug, data);
  return data;
}

/** Synchronous lookup: `{ hit: false }` when the slug was never preloaded. */
export function getPreloadedCondition(slug: string): { hit: boolean; data?: ConditionPageData } {
  if (!preloadedConditions.has(slug)) return { hit: false };
  return { hit: true, data: preloadedConditions.get(slug) };
}

/**
 * The short name a condition shows in lists, the nav menu, search results,
 * related links and blog topic labels: `navLabel` when the page title is a
 * headline sentence, otherwise the title itself.
 */
export function conditionLabel(condition: { title: string; navLabel?: string }): string {
  return condition.navLabel ?? condition.title;
}

let labelBySlug: Map<string, string> | null = null;
/** conditionLabel() looked up by slug; undefined for unknown slugs. */
export function getConditionLabelBySlug(slug: string): string | undefined {
  if (!labelBySlug) labelBySlug = new Map(CONDITION_META.map((c) => [c.slug, conditionLabel(c)]));
  return labelBySlug.get(slug);
}

/** True when the query matches the condition's label, title or subtitle. */
export function conditionMatchesQuery(condition: ConditionMeta, normalizedQuery: string): boolean {
  return (
    conditionLabel(condition).toLowerCase().includes(normalizedQuery) ||
    condition.title.toLowerCase().includes(normalizedQuery) ||
    condition.subtitle.toLowerCase().includes(normalizedQuery)
  );
}

export function getAllConditions(): ConditionMeta[] {
  return CONDITION_META;
}

export function getNavConditions(): ConditionMeta[] {
  return CONDITION_META.filter((c) => NAV_CONDITION_SLUGS.has(c.slug));
}

export function getConditionsByCategory(category: ConditionCategory): ConditionMeta[] {
  return CONDITION_META.filter((c) => c.category === category);
}

export function getGroupedConditions(): Array<{
  category: ConditionCategory;
  label: string;
  conditions: ConditionMeta[];
}> {
  return CATEGORY_ORDER
    .map((cat) => ({
      category: cat,
      label: CATEGORY_LABELS[cat],
      conditions: getConditionsByCategory(cat),
    }))
    .filter((g) => g.conditions.length > 0);
}

export function getGroupedNavConditions(): Array<{
  category: ConditionCategory;
  label: string;
  conditions: ConditionMeta[];
}> {
  return CATEGORY_ORDER
    .map((cat) => ({
      category: cat,
      label: CATEGORY_LABELS[cat],
      conditions: CONDITION_META.filter(
        (c) => c.category === cat && NAV_CONDITION_SLUGS.has(c.slug)
      ),
    }))
    .filter((g) => g.conditions.length > 0);
}

/**
 * Find a condition whose title matches the given text (case-insensitive).
 * Returns the slug if found, null otherwise.
 * Checks exact match first, then substring containment in both directions.
 */
export function findConditionSlugByTitle(text: string): string | null {
  const normalized = text.toLowerCase().trim();

  // Exact match
  for (const c of CONDITION_META) {
    if (c.title.toLowerCase() === normalized) return c.slug;
    if (c.navLabel && c.navLabel.toLowerCase() === normalized) return c.slug;
  }

  // Substring: condition title contained in text, or text contained in condition title
  for (const c of CONDITION_META) {
    const condTitle = c.title.toLowerCase();
    if (normalized.length >= 4 && condTitle.includes(normalized)) return c.slug;
    if (condTitle.length >= 4 && normalized.includes(condTitle)) return c.slug;
  }

  return null;
}

export { CATEGORY_ORDER, CATEGORY_LABELS };
export type { ConditionPageData, ConditionCategory, ConditionMeta };
